import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useComplianceTypes } from "../../hooks/useComplianceTypes";
import { ComplianceTypeEntity } from "../../../domain/entities/ComplianceTypeEntity";
import { ComplianceTypeForm } from "../../components/ComplianceTypeForm";
import { Table } from "../../../../../ui/components/Table";
import { Button } from "../../../../../ui/components/Button";
import { Badge } from "../../../../../ui/components/Badge";
import {
  FeatherPlus,
  FeatherEdit,
  FeatherTrash2,
  FeatherArrowLeft,
  FeatherCalendar,
} from "@subframe/core";

export const ComplianceTypesPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    complianceTypes,
    loading,
    error,
    fetchComplianceTypes,
    createComplianceType,
    updateComplianceType,
    deleteComplianceType,
  } = useComplianceTypes();
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<ComplianceTypeEntity | null>(null);
  const [deletingTypeId, setDeletingTypeId] = useState<number | null>(null);

  useEffect(() => {
    fetchComplianceTypes();
  }, [fetchComplianceTypes]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this compliance type?")) {
      return;
    }

    try {
      setDeletingTypeId(id);
      await deleteComplianceType(id);
    } catch (err: any) {
      alert(err.message || "Failed to delete compliance type");
    } finally {
      setDeletingTypeId(null);
    }
  };

  const handleEdit = (type: ComplianceTypeEntity) => {
    setEditingType(type);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingType(null);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<ComplianceTypeEntity>) => {
    try {
      if (editingType) {
        await updateComplianceType(editingType.id, data);
      } else {
        await createComplianceType(data);
      }
      setShowForm(false);
      setEditingType(null);
    } catch (err: any) {
      alert(err.message || "Failed to save compliance type");
    }
  };

  return (
    <div className="flex h-screen w-full flex-col bg-default-background">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading compliance types...</p>
          </div>
        </div>
      )}
      <div className="flex w-full flex-col items-start gap-6 p-12">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-secondary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/admin/dashboard?tab=compliance")}
            >
              Back
            </Button>
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Compliance Types
              </span>
              <span className="text-body font-body text-subtext-color">
                Manage compliance types and their configurations
              </span>
            </div>
          </div>
          <Button
            icon={<FeatherPlus />}
            onClick={handleAdd}
          >
            Create Compliance Type
          </Button>
        </div>

        {error && (
          <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-sm font-medium text-red-800">
              {error.message}
            </span>
          </div>
        )}

        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Compliance Type</Table.HeaderCell>
              <Table.HeaderCell>Compliance Name</Table.HeaderCell>
              <Table.HeaderCell>Authority</Table.HeaderCell>
              <Table.HeaderCell>Frequency</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {complianceTypes.map((type) => (
            <Table.Row key={type.id}>
              <Table.Cell>
                <span className="text-body-bold font-body-bold text-default-font">
                  {type.compliance_type}
                </span>
              </Table.Cell>
              <Table.Cell>
                <span className="text-body font-body text-neutral-500">
                  {type.compliance_name}
                </span>
              </Table.Cell>
              <Table.Cell>
                <span className="text-body font-body text-neutral-500">
                  {type.authority}
                </span>
              </Table.Cell>
              <Table.Cell>
                <span className="text-body font-body text-neutral-500">
                  {type.frequency}
                </span>
              </Table.Cell>
              <Table.Cell>
                <Badge variant={type.is_active ? "success" : "error"}>
                  {type.is_active ? "Active" : "Inactive"}
                </Badge>
              </Table.Cell>
              <Table.Cell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="brand-secondary"
                    size="small"
                    icon={<FeatherCalendar />}
                    onClick={() => navigate(`/admin/compliance/calendar?typeId=${type.id}`)}
                  >
                    Create Entries
                  </Button>
                  <Button
                    variant="neutral-tertiary"
                    size="small"
                    icon={<FeatherEdit />}
                    onClick={() => handleEdit(type)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="destructive-tertiary"
                    size="small"
                    icon={<FeatherTrash2 />}
                    onClick={() => handleDelete(type.id)}
                    loading={deletingTypeId === type.id}
                    disabled={deletingTypeId === type.id || loading}
                  >
                    Delete
                  </Button>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>

        {complianceTypes.length === 0 && !loading && (
          <div className="flex w-full items-center justify-center p-8 text-body font-body text-subtext-color">
            No compliance types found. Create your first compliance type to get started.
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-heading-3 font-heading-3 mb-4">
              {editingType ? "Edit Compliance Type" : "Create Compliance Type"}
            </h3>
            <ComplianceTypeForm
              initialData={editingType}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingType(null);
              }}
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

